<template>
  <div
    class="bg-white rounded-lg shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black mb-8 max-w-5xl mx-auto"
  >
    <!-- Top Section - Flashcards Overview -->
    <div class="flex items-start justify-between p-6 bg-green-50 rounded-t-lg">
      <div class="flex-1">
        <div class="flex items-center mb-3">
          <DocumentDuplicateIcon class="w-6 h-6 text-green-800 mr-2" />
          <h2 class="text-xl font-bold text-green-800">Flashcards: {{ displayTitle }}</h2>
        </div>

        <div class="flex items-center space-x-4 text-sm text-green-800 ml-1">
          <div class="flex items-center max-w-xs">
            <BookOpenIcon class="w-4 h-4 text-green-800 mr-2 flex-shrink-0" />
            <span class="truncate" :title="displaySubject">{{ displaySubject }}</span>
          </div>
          <div class="flex items-center">
            <CalendarIcon class="w-4 h-4 text-green-800 mr-2" />
            Generated {{ displayDate }}
          </div>
          <div class="flex items-center">
            <DocumentTextIcon class="w-4 h-4 text-green-800 mr-2" />
            {{ displaySource }}
          </div>
          <div class="flex items-center">
            <CreditCardIcon class="w-4 h-4 text-green-800 mr-2" />
            {{ flashcardsData?.flashcards?.length || 0 }} cards
          </div>
          <div class="flex items-center" v-if="savedFlashcardsId">
            <svg class="w-4 h-4 text-green-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd"
              />
            </svg>
            <span class="text-green-600 text-xs font-medium">Saved to Library</span>
          </div>
        </div>
      </div>

      <div class="text-right">
        <span
          class="border border-green-800 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium"
        >
          Flashcards
        </span>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="p-6">
      <!-- AI Content Notice Section -->
      <div class="mb-6 border-2 border-yellow-600 rounded-md p-6 bg-yellow-100/25">
        <div class="flex items-center mb-3">
          <svg
            class="w-5 h-5 text-yellow-700 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
          <h3 class="text-lg font-semibold text-yellow-700">AI Content Notice</h3>
        </div>
        <p class="text-gray-700 leading-relaxed">
          <strong class="font-semibold">Important:</strong> The AI-generated flashcards in this app
          are for reference only and may contain errors. We encourage you to double-check the
          information and consult with your teachers or trusted sources. As of October 2025, this
          app uses the Google Gemini 2.5 Flash model to assist in processing notes. If you find
          anything unusual, please report it using the button or email us at
          <a
            href="mailto:elevated.tutorapp@gmail.com"
            class="underline hover:text-yellow-800 text-yellow-700"
            >elevated.tutorapp@gmail.com</a
          >.
        </p>
      </div>

      <!-- Flashcards Section -->
      <div class="mb-6 border-2 border-green-800 rounded-md p-6 bg-green-100/25">
        <div class="flex items-center justify-between mb-6">
          <div class="flex items-center">
            <svg
              class="w-5 h-5 text-green-800 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            <h3 class="text-lg font-semibold text-green-800">Interactive Flashcards</h3>
          </div>

          <!-- Card Counter -->
          <div class="text-green-800 font-medium">
            {{ currentCardIndex + 1 }} / {{ flashcardsData?.flashcards?.length || 0 }}
          </div>
        </div>

        <!-- Flashcard Display Area -->
        <div v-if="flashcardsData && flashcardsData.flashcards.length > 0" class="mb-6">
          <!-- Single Flashcard Container -->
          <div class="flex justify-center mb-6">
            <div
              class="flashcard-container relative w-full max-w-2xl h-80 cursor-pointer"
              @click="flipCard"
              style="perspective: 1000px"
            >
              <div
                class="flashcard w-full h-full relative transition-transform duration-700 preserve-3d"
                :class="{ 'is-flipped': isFlipped }"
                style="transform-style: preserve-3d"
              >
                <!-- Front of Card -->
                <div
                  class="flashcard-face flashcard-front absolute inset-0 w-full h-full bg-white border-3 border-black rounded-xl p-6 flex items-center justify-center shadow-[6px_6px_0_0_rgba(0,0,0,1)] overflow-hidden"
                  style="backface-visibility: hidden"
                >
                  <div class="text-center max-h-full overflow-y-auto px-4 w-full">
                    <div class="text-sm text-gray-500 mb-4 font-medium">FRONT</div>
                    <div
                      class="text-xl font-bold text-gray-900 leading-relaxed break-words"
                      v-html="formatCorrectedTags(currentCard?.front || 'No question available')"
                    ></div>
                    <div class="text-sm text-gray-400 mt-6">Click to reveal answer</div>
                  </div>
                </div>

                <!-- Back of Card -->
                <div
                  class="flashcard-face flashcard-back absolute inset-0 w-full h-full bg-gradient-to-br from-green-50 to-green-100 border-3 border-green-600 rounded-xl p-6 flex items-center justify-center shadow-[6px_6px_0_0_rgba(34,197,94,1)] overflow-hidden"
                  style="backface-visibility: hidden; transform: rotateY(180deg)"
                >
                  <div class="text-center max-h-full overflow-y-auto px-4 w-full">
                    <div class="text-sm text-green-600 mb-4 font-medium">BACK</div>
                    <div
                      class="text-lg font-semibold text-gray-900 leading-relaxed break-words"
                      v-html="formatCorrectedTags(currentCard?.back || 'No answer available')"
                    ></div>
                    <div class="text-sm text-green-600 mt-6">Click to show question</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Navigation Controls -->
          <div class="flex justify-center items-center space-x-6">
            <!-- Previous Button -->
            <button
              @click="previousCard"
              :disabled="currentCardIndex === 0"
              :class="[
                'flex items-center px-6 py-3 rounded-xl font-bold text-white shadow-[4px_4px_0_0_rgba(0,0,0,1)] border-2 border-black transition-all duration-200',
                currentCardIndex === 0
                  ? 'bg-gray-400 cursor-not-allowed opacity-50'
                  : 'bg-blue-600 hover:bg-blue-700 hover:transform hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_0_rgba(0,0,0,1)] cursor-pointer',
              ]"
            >
              <ChevronLeftIcon class="w-5 h-5 mr-2" />
              Previous
            </button>

            <!-- Card Flip Button -->
            <button
              @click="flipCard"
              class="flex items-center px-6 py-3 rounded-xl font-bold text-white bg-green-600 hover:bg-green-700 shadow-[4px_4px_0_0_rgba(0,0,0,1)] border-2 border-black transition-all duration-200 hover:transform hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_0_rgba(0,0,0,1)] cursor-pointer"
            >
              <ArrowPathIcon class="w-5 h-5 mr-2" />
              {{ isFlipped ? "Show Question" : "Show Answer" }}
            </button>

            <!-- Next Button -->
            <button
              @click="nextCard"
              :disabled="currentCardIndex === (flashcardsData?.flashcards?.length || 1) - 1"
              :class="[
                'flex items-center px-6 py-3 rounded-xl font-bold text-white shadow-[4px_4px_0_0_rgba(0,0,0,1)] border-2 border-black transition-all duration-200',
                currentCardIndex === (flashcardsData?.flashcards?.length || 1) - 1
                  ? 'bg-gray-400 cursor-not-allowed opacity-50'
                  : 'bg-blue-600 hover:bg-blue-700 hover:transform hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_0_rgba(0,0,0,1)] cursor-pointer',
              ]"
            >
              Next
              <ChevronRightIcon class="w-5 h-5 ml-2" />
            </button>
          </div>

          <!-- Progress Bar -->
          <div class="mt-6">
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div
                class="bg-green-600 h-2 rounded-full transition-all duration-300"
                :style="{ width: `${progressPercentage}%` }"
              ></div>
            </div>
            <div class="text-center text-sm text-gray-600 mt-2">
              Progress: {{ Math.round(progressPercentage) }}%
            </div>
          </div>
        </div>

        <!-- No Flashcards Available -->
        <div v-else class="text-center py-12">
          <CreditCardIcon class="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p class="text-gray-600 text-lg">
            No flashcards available. Please generate content first.
          </p>
        </div>
      </div>

      <!-- Sources Section - Only show if there are real credible URLs -->
      <div
        v-if="hasRealSources"
        class="mb-6 border-2 border-orange-800 rounded-md p-6 bg-orange-100/25"
      >
        <div class="flex items-center mb-3">
          <svg
            class="w-5 h-5 text-orange-800 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C20.832 18.477 19.246 18 17.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
          <h3 class="text-lg font-semibold text-orange-800">Additional Sources & References</h3>
        </div>
        <div
          class="text-sm text-orange-900 mb-3 p-3 bg-orange-200/50 rounded border-l-4 border-orange-600"
        >
          <strong>AI Content Notice:</strong> The content provided was generated by AI and
          cross-referenced among the additional sources below. Always verify information
          independently for academic use.
        </div>
        <ul class="space-y-3 text-gray-700 ml-7">
          <li
            v-for="(source, index) in flashcardsData?.sources || []"
            :key="index"
            class="flex items-start"
          >
            <span class="text-orange-600 mr-2 mt-1 flex-shrink-0">{{ index + 1 }}.</span>
            <span
              class="break-words break-all overflow-wrap-anywhere"
              v-html="formatSourceWithLinks(source)"
            ></span>
          </li>
        </ul>
      </div>
    </div>

    <!-- Export Button -->
    <div class="flex justify-center px-6 mb-10">
      <button
        @click="exportFlashcards"
        :disabled="!flashcardsData"
        :class="[
          'relative overflow-hidden px-12 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-bold text-lg shadow-[4px_4px_0_0_rgba(0,0,0,1)] border-2 border-black hover:from-green-500 hover:to-emerald-500 hover:transform hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_0_rgba(0,0,0,1)] transition-all duration-300 ease-in-out min-w-[280px] whitespace-nowrap before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700 before:ease-in-out flex items-center justify-center',
          !flashcardsData ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
        ]"
      >
        <ArrowDownTrayIcon class="w-6 h-6 inline mr-3" />
        Export Flashcards to PDF
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import {
  DocumentDuplicateIcon,
  BookOpenIcon,
  CalendarIcon,
  DocumentTextIcon,
  CreditCardIcon,
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowPathIcon,
} from "@heroicons/vue/24/outline";
import { useAIContentStore } from "@/stores/aiContentStore";
import { logInfo, logError, logSuccess, logDebug } from "@/utils/secureLogger";
import { htmlPdfExportService as pdfExportService } from "@/utils/htmlPdfExport";
import { sanitizeHtml } from "@/utils/xssSanitizer";
import { renderLatex } from "@/utils/latexRenderer";

const router = useRouter();
const aiContentStore = useAIContentStore();

// Flashcard state
const currentCardIndex = ref(0);
const isFlipped = ref(false);

// Check if we have generated flashcards, if not redirect back
onMounted(() => {
  document.title = "ElevatED – AI Flashcards";
  if (!aiContentStore.generatedFlashcards) {
    // No content available, redirect back to generator
    logInfo("No generated flashcards available, redirecting to study materials");
    router.push("/student/study-materials");
  }
});

// Computed properties for dynamic content
const flashcardsData = computed(() => aiContentStore.generatedFlashcards);
const displayTitle = computed(() => flashcardsData.value?.topic || "AI Generated Flashcards");
const displaySubject = computed(() => flashcardsData.value?.subject || "Study Material");
const displayDate = computed(() => aiContentStore.lastGeneratedDate || "Today");
const displaySource = computed(() => aiContentStore.sourceFileName || "Unknown Source");
const savedFlashcardsId = computed(() => aiContentStore.savedFlashcardsId);

const currentCard = computed(() => {
  if (
    !flashcardsData.value ||
    !flashcardsData.value.flashcards ||
    flashcardsData.value.flashcards.length === 0
  ) {
    return null;
  }
  return flashcardsData.value.flashcards[currentCardIndex.value];
});

const progressPercentage = computed(() => {
  if (
    !flashcardsData.value ||
    !flashcardsData.value.flashcards ||
    flashcardsData.value.flashcards.length === 0
  ) {
    return 0;
  }
  return ((currentCardIndex.value + 1) / flashcardsData.value.flashcards.length) * 100;
});

// Navigation functions
const nextCard = () => {
  if (flashcardsData.value && currentCardIndex.value < flashcardsData.value.flashcards.length - 1) {
    currentCardIndex.value++;
    isFlipped.value = false; // Reset flip state when moving to next card
    logDebug("Navigated to next flashcard", {
      cardIndex: currentCardIndex.value,
      totalCards: flashcardsData.value.flashcards.length,
    });
  }
};

const previousCard = () => {
  if (currentCardIndex.value > 0) {
    currentCardIndex.value--;
    isFlipped.value = false; // Reset flip state when moving to previous card
    logDebug("Navigated to previous flashcard", {
      cardIndex: currentCardIndex.value,
      totalCards: flashcardsData.value?.flashcards.length || 0,
    });
  }
};

const flipCard = () => {
  isFlipped.value = !isFlipped.value;
  logDebug("Flipped flashcard", {
    cardIndex: currentCardIndex.value,
    isShowingBack: isFlipped.value,
    front: currentCard.value?.front?.substring(0, 50),
    back: currentCard.value?.back?.substring(0, 50),
  });
};

// Reset flip state when card changes
watch(currentCardIndex, () => {
  isFlipped.value = false;
});

// Check if sources contain real URLs (not just generic fallback text)
const hasRealSources = computed(() => {
  const sources = flashcardsData.value?.sources || [];
  if (sources.length === 0) return false;

  const urlPattern = /^https?:\/\//i;
  const genericFallbacks = [
    "AI-generated",
    "verify with",
    "authoritative sources",
    "flashcards - verify",
  ];

  const realUrlCount = sources.filter((source) => {
    // Check if it's a URL
    if (!urlPattern.test(source)) return false;

    // Check if it contains generic fallback text
    const hasGenericText = genericFallbacks.some((fallback) =>
      source.toLowerCase().includes(fallback.toLowerCase())
    );

    return !hasGenericText;
  }).length;

  return realUrlCount > 0;
});

// Format source text in full APA 7th Edition format
const formatSourceWithLinks = (source: string): string => {
  if (!source) return "";

  // Extract URL from source string
  const urlRegex = /(https?:\/\/[^\s]+)/gi;
  const urls = source.match(urlRegex);

  if (!urls || urls.length === 0) {
    return sanitizeHtml(source);
  }

  const url = urls[0];

  try {
    const urlObj = new URL(url);
    const domain = urlObj.hostname.replace("www.", "");

    // Get current date for retrieval date
    const now = new Date();
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const retrievalDate = `${months[now.getMonth()]} ${now.getDate()}, ${now.getFullYear()}`;

    // Extract organization/author from domain
    let author = "";
    if (domain.includes(".edu")) {
      // Educational institutions
      const parts = domain.split(".");
      author = parts[0]
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    } else if (domain.includes(".gov")) {
      // Government sites
      const parts = domain.split(".");
      author = parts[0]
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    } else {
      // Default: use domain as author
      author = domain.split(".")[0].charAt(0).toUpperCase() + domain.split(".")[0].slice(1);
    }

    // Extract title from source (text before URL) or use domain-based title
    let title = source.split(url)[0].trim();
    if (!title || title.length < 5) {
      title = "Educational resource";
    }

    // Build APA 7th Edition citation
    // Format: Author. (Year). Title. Retrieved Month Day, Year, from URL
    const apaFormatted = `
      <span class="font-semibold">${sanitizeHtml(author)}.</span>
      <span class="italic">${sanitizeHtml(title)}</span>.
      Retrieved ${retrievalDate}, from
      <a href="${url}" target="_blank" rel="noopener noreferrer" 
         class="text-blue-600 hover:text-blue-800 underline inline-block ml-1" 
         style="word-break: break-all; overflow-wrap: anywhere;">${url}</a>
    `;

    return sanitizeHtml(apaFormatted);
  } catch (error) {
    // Fallback: Just make the URL clickable
    const fallback = source.replace(
      urlRegex,
      (matchedUrl) =>
        `<a href="${matchedUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline inline-block" style="word-break: break-all; overflow-wrap: anywhere;">${matchedUrl}</a>`
    );
    return sanitizeHtml(fallback);
  }
};

/**
 * ✅ Format text with CORRECTED tags for better visual presentation
 * Converts [CORRECTED FROM: source] tags into styled badges
 * ✅ Also renders LaTeX mathematical formulas using KaTeX
 */
const formatCorrectedTags = (text: string): string => {
  if (!text) return "";

  // Pattern: [CORRECTED FROM: source name]
  const correctedPattern = /\[CORRECTED FROM:\s*([^\]]+)\]/gi;

  // Replace CORRECTED tags with styled HTML badges
  let formatted = text.replace(correctedPattern, (match, sourceName) => {
    return `<span class="corrected-badge">
        <svg class="corrected-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span class="corrected-text">Fact-checked</span>
        <span class="corrected-source">${sanitizeHtml(sourceName.trim())}</span>
      </span>`;
  });

  // Render LaTeX formulas (e.g., $x^2 + y^2 = r^2$ or $$\frac{a}{b}$$)
  formatted = renderLatex(formatted);

  return formatted;
};

// Export functionality
const exportFlashcards = async () => {
  if (!flashcardsData.value) {
    logError("Export failed: No flashcard data available");
    return;
  }

  try {
    logInfo("Starting flashcard PDF export", {
      flashcardCount: flashcardsData.value.flashcards.length,
      topic: flashcardsData.value.topic,
      subject: flashcardsData.value.subject,
    });

    // Create a material object compatible with the PDF export service
    const material = {
      id: savedFlashcardsId.value || `temp_flashcards_${Date.now()}`,
      title: displayTitle.value,
      type: "Flashcards" as const,
      subject: displaySubject.value,
      source: displaySource.value,
      date: new Date(),
      description: `${flashcardsData.value.flashcards.length} interactive flashcards for ${flashcardsData.value.topic}`,
      keyPoints: `Generated ${flashcardsData.value.flashcards.length} flashcards covering key concepts`,
      flashcards: flashcardsData.value.flashcards,
      sources: flashcardsData.value.sources || [
        "AI-generated flashcards - verify with authoritative sources",
      ],
    };

    // Use the PDF export service with flashcard-specific formatting
    await pdfExportService.exportFlashcardsToPDF(material);

    logSuccess("Flashcards exported to PDF successfully", {
      flashcardCount: flashcardsData.value.flashcards.length,
      fileName: `${displayTitle.value}_Flashcards.pdf`,
    });
  } catch (error) {
    logError("PDF export failed", error);
  }
};
</script>

<style scoped>
/* LaTeX Rendering Styles */
:deep(.katex-display-wrapper) {
  margin: 1rem 0;
  text-align: center;
  overflow-x: auto;
  overflow-y: hidden;
}

:deep(.katex-inline-wrapper) {
  display: inline-block;
  margin: 0 0.2rem;
}

:deep(.latex-error) {
  background-color: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 4px;
  padding: 2px 6px;
  font-family: "Courier New", monospace;
  font-size: 0.9em;
  color: #92400e;
}

/* KaTeX math rendering */
:deep(.katex) {
  font-size: 1.1em;
}

:deep(.katex-display) {
  margin: 1rem 0;
  overflow-x: auto;
  overflow-y: hidden;
  text-align: center;
}

/* Flashcard styling */
.flashcard-container {
  perspective: 1000px;
}

.flashcard {
  transform-style: preserve-3d;
  transition: transform 0.7s cubic-bezier(0.4, 0, 0.2, 1);
}

.flashcard.is-flipped {
  transform: rotateY(180deg);
}

.flashcard-face {
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}

.flashcard-front {
  transform: rotateY(0deg);
}

.flashcard-back {
  transform: rotateY(180deg);
}

/* Improve card hover effects */
.flashcard-container:hover .flashcard-face {
  box-shadow: 8px 8px 0 0 rgba(0, 0, 0, 1);
}

/* Scrollbar styling for overflow content */
.flashcard-face :deep(div) {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.flashcard-face :deep(div::-webkit-scrollbar) {
  width: 6px;
}

.flashcard-face :deep(div::-webkit-scrollbar-track) {
  background: transparent;
}

.flashcard-face :deep(div::-webkit-scrollbar-thumb) {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.flashcard-face :deep(div::-webkit-scrollbar-thumb:hover) {
  background-color: rgba(0, 0, 0, 0.3);
}

/* Smooth transitions for navigation buttons */
button[disabled] {
  transform: none !important;
  box-shadow: 4px 4px 0 0 rgba(0, 0, 0, 1) !important;
}

/* Progress bar animation */
.bg-green-600 {
  transition: width 0.3s ease-in-out;
}

/* Corrected/Fact-checked Badge Styling */
:deep(.corrected-badge) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 2px solid #065f46;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
  margin-left: 8px;
  white-space: nowrap;
  transition: all 0.2s ease;
}

:deep(.corrected-badge:hover) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(16, 185, 129, 0.4);
}

:deep(.corrected-icon) {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

:deep(.corrected-text) {
  font-weight: 700;
  letter-spacing: 0.3px;
}

:deep(.corrected-source) {
  font-weight: 500;
  opacity: 0.95;
  font-style: italic;
  padding-left: 4px;
  border-left: 1px solid rgba(255, 255, 255, 0.4);
}

/* Responsive: Stack badge on small screens */
@media (max-width: 640px) {
  :deep(.corrected-badge) {
    display: flex;
    margin-left: 0;
    margin-top: 8px;
  }
}
</style>
